import { motion } from "framer-motion";
import {
  X,
  Phone,
  Mail,
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Star,
  Edit2,
  Trash2,
  Building2,
  MapPin,
  Calendar,
  Clock,
  MessageSquare,
  ExternalLink,
  Copy,
  Check,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/utils/cn";
import type { Contact } from "./ContactsPage";

const channelIcons = {
  whatsapp: { icon: MessageCircle, color: "#22C55E", label: "WhatsApp" },
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  widget: { icon: Globe, color: "#2563EB", label: "Chat Widget" },
};

const statusStyles = {
  customer: { bg: "bg-green-50", text: "text-green-600", label: "Cliente" },
  lead: { bg: "bg-amber-50", text: "text-amber-600", label: "Lead" },
  active: { bg: "bg-blue-50", text: "text-blue-600", label: "Ativo" },
  inactive: { bg: "bg-gray-50", text: "text-gray-500", label: "Inativo" },
};

const tagColors: Record<string, { bg: string; text: string }> = {
  VIP: { bg: "bg-purple-100", text: "text-purple-700" },
  Enterprise: { bg: "bg-indigo-100", text: "text-indigo-700" },
  Parceiro: { bg: "bg-cyan-100", text: "text-cyan-700" },
  Lead: { bg: "bg-amber-100", text: "text-amber-700" },
  Startup: { bg: "bg-pink-100", text: "text-pink-700" },
  Premium: { bg: "bg-yellow-100", text: "text-yellow-700" },
  Agência: { bg: "bg-teal-100", text: "text-teal-700" },
  "E-commerce": { bg: "bg-orange-100", text: "text-orange-700" },
  Tech: { bg: "bg-blue-100", text: "text-blue-700" },
  Consultoria: { bg: "bg-rose-100", text: "text-rose-700" },
  Marketing: { bg: "bg-fuchsia-100", text: "text-fuchsia-700" },
  Logística: { bg: "bg-lime-100", text: "text-lime-700" },
  Fintech: { bg: "bg-emerald-100", text: "text-emerald-700" },
  Indústria: { bg: "bg-slate-100", text: "text-slate-700" },
};

// Mock conversation history
const conversationHistory = [
  {
    id: 1,
    message: "Olá, gostaria de saber mais sobre os planos disponíveis",
    type: "received",
    time: "10:30",
    date: "Hoje",
  },
  {
    id: 2,
    message: "Claro! Temos três planos: Starter, Business e Enterprise. Qual seria mais adequado para sua empresa?",
    type: "sent",
    time: "10:32",
    date: "Hoje",
  },
  {
    id: 3,
    message: "O Enterprise parece interessante. Qual o valor?",
    type: "received",
    time: "10:35",
    date: "Hoje",
  },
  {
    id: 4,
    message: "O plano Enterprise custa R$ 499/mês e inclui todas as funcionalidades + suporte prioritário",
    type: "sent",
    time: "10:38",
    date: "Hoje",
  },
];

interface ContactDetailsProps {
  contact: Contact;
  onClose: () => void;
  onToggleFavorite: (id: string) => void;
}

export function ContactDetails({
  contact,
  onClose,
  onToggleFavorite,
}: ContactDetailsProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const ChannelIcon = channelIcons[contact.channel].icon;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="w-[420px] flex-shrink-0 border-l border-[#E2E8F0] bg-white overflow-y-auto"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E2E8F0] bg-white px-6 py-4">
        <h2 className="text-lg font-semibold text-[#0F172A]">Detalhes do Contato</h2>
        <button
          onClick={onClose}
          className="rounded-lg p-2 text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Profile Section */}
      <div className="px-6 py-6 border-b border-[#E2E8F0]">
        <div className="flex items-start gap-4">
          <div className="relative">
            <img
              src={contact.avatar}
              alt={contact.name}
              className="h-16 w-16 rounded-full object-cover ring-4 ring-[#F1F5F9]"
            />
            <div
              className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white"
              style={{ backgroundColor: channelIcons[contact.channel].color }}
            >
              <ChannelIcon className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-[#0F172A] truncate">
                {contact.name}
              </h3>
              <button
                onClick={() => onToggleFavorite(contact.id)}
                className={cn(
                  "transition-colors",
                  contact.isFavorite
                    ? "text-amber-400"
                    : "text-[#CBD5E1] hover:text-amber-400"
                )}
              >
                <Star
                  className="h-5 w-5"
                  fill={contact.isFavorite ? "currentColor" : "none"}
                />
              </button>
            </div>
            <p className="text-sm text-[#64748B]">{contact.role}</p>
            <span
              className={cn(
                "mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
                statusStyles[contact.status].bg,
                statusStyles[contact.status].text
              )}
            >
              {statusStyles[contact.status].label}
            </span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex gap-2">
          <button className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#2563EB] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1E40AF]">
            <MessageSquare className="h-4 w-4" />
            Iniciar Conversa
          </button>
          <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E2E8F0] text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#0F172A]">
            <Phone className="h-4 w-4" />
          </button>
          <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E2E8F0] text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#0F172A]">
            <Mail className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Contact Info */}
      <div className="px-6 py-6 border-b border-[#E2E8F0]">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
          Informações de Contato
        </h4>
        <div className="space-y-4">
          <InfoRow
            icon={Mail}
            label="Email"
            value={contact.email}
            onCopy={() => handleCopy(contact.email, "email")}
            copied={copiedField === "email"}
          />
          <InfoRow
            icon={Phone}
            label="Telefone"
            value={contact.phone}
            onCopy={() => handleCopy(contact.phone, "phone")}
            copied={copiedField === "phone"}
          />
          <InfoRow
            icon={Building2}
            label="Empresa"
            value={contact.company}
          />
          <InfoRow
            icon={MapPin}
            label="Localização"
            value={contact.location}
          />
          <InfoRow
            icon={Calendar}
            label="Criado em"
            value={contact.createdAt}
          />
          <InfoRow
            icon={Clock}
            label="Último contato"
            value={contact.lastContact}
          />
        </div>
      </div>

      {/* Tags */}
      <div className="px-6 py-6 border-b border-[#E2E8F0]">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
          Tags
        </h4>
        <div className="flex flex-wrap gap-2">
          {contact.tags.map((tag) => (
            <span
              key={tag}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium",
                tagColors[tag]?.bg || "bg-gray-100",
                tagColors[tag]?.text || "text-gray-600"
              )}
            >
              {tag}
            </span>
          ))}
          <button className="rounded-full border border-dashed border-[#CBD5E1] px-3 py-1 text-xs font-medium text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]">
            + Adicionar
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="px-6 py-6 border-b border-[#E2E8F0]">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
          Estatísticas
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <StatBox label="Conversas" value={contact.totalConversations} color="#2563EB" />
          <StatBox label="Tempo Médio" value="12min" color="#22C55E" />
          <StatBox label="Satisfação" value="98%" color="#8B5CF6" />
          <StatBox label="Tickets" value="5" color="#F59E0B" />
        </div>
      </div>

      {/* Recent Conversations */}
      <div className="px-6 py-6">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
            Conversas Recentes
          </h4>
          <button className="flex items-center gap-1 text-xs font-medium text-[#2563EB] hover:underline">
            Ver todas <ExternalLink className="h-3 w-3" />
          </button>
        </div>
        <div className="space-y-3">
          {conversationHistory.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "rounded-xl p-3",
                msg.type === "sent" ? "bg-[#2563EB]/5 ml-4" : "bg-[#F8FAFC] mr-4"
              )}
            >
              <p className="text-sm text-[#0F172A]">{msg.message}</p>
              <p className="mt-1 text-xs text-[#94A3B8]">
                {msg.date} às {msg.time}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="sticky bottom-0 flex gap-3 border-t border-[#E2E8F0] bg-white px-6 py-4">
        <button className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#E2E8F0] py-2.5 text-sm font-medium text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#0F172A]">
          <Edit2 className="h-4 w-4" />
          Editar
        </button>
        <button className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#E2E8F0] py-2.5 text-sm font-medium text-[#EF4444] transition-colors hover:bg-red-50">
          <Trash2 className="h-4 w-4" />
          Excluir
        </button>
      </div>
    </motion.div>
  );
}

interface InfoRowProps {
  icon: React.ElementType;
  label: string;
  value: string;
  onCopy?: () => void;
  copied?: boolean;
}

function InfoRow({ icon: Icon, label, value, onCopy, copied }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F8FAFC]">
          <Icon className="h-4 w-4 text-[#64748B]" />
        </div>
        <div>
          <p className="text-xs text-[#94A3B8]">{label}</p>
          <p className="text-sm text-[#0F172A]">{value}</p>
        </div>
      </div>
      {onCopy && (
        <button
          onClick={onCopy}
          className="rounded-lg p-1.5 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#64748B]"
        >
          {copied ? (
            <Check className="h-4 w-4 text-[#22C55E]" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  );
}

interface StatBoxProps {
  label: string;
  value: string | number;
  color: string;
}

function StatBox({ label, value, color }: StatBoxProps) {
  return (
    <div className="rounded-xl bg-[#F8FAFC] p-3">
      <p className="text-xl font-bold" style={{ color }}>
        {value}
      </p>
      <p className="text-xs text-[#64748B]">{label}</p>
    </div>
  );
}